'use client'

import { useEffect, useState } from 'react'

type SeasonalTheme = 'ramadan' | 'eid' | 'hajj' | 'national_day'

interface SeasonalDecorationsProps {
  theme: string | null
}

export function SeasonalDecorations({ theme }: SeasonalDecorationsProps) {
  const [mounted, setMounted] = useState(false)

  useEffect(() => {
    setMounted(true)
  }, [])

  if (!mounted || !theme) return null

  const validTheme = theme as SeasonalTheme
  const ThemeComponent = THEME_MAP[validTheme]
  if (!ThemeComponent) return null

  return (
    <>
      <style dangerouslySetInnerHTML={{ __html: SEASONAL_KEYFRAMES }} />
      <div
        aria-hidden="true"
        className="pointer-events-none fixed inset-x-0 bottom-0 z-0 overflow-hidden"
        style={{ isolation: 'isolate', top: '64px' }}
      >
        <ThemeComponent />
      </div>
    </>
  )
}

// ---------------------------------------------------------------------------
// Shared keyframes for all themes
// ---------------------------------------------------------------------------
const SEASONAL_KEYFRAMES = `
@keyframes sd-sway {
  0%, 100% { transform: rotate(-3deg); }
  50% { transform: rotate(3deg); }
}
@keyframes sd-sway-slow {
  0%, 100% { transform: rotate(-2deg) translateX(-2px); }
  50% { transform: rotate(2deg) translateX(2px); }
}
@keyframes sd-twinkle {
  0%, 100% { opacity: 0.3; transform: scale(0.8); }
  50% { opacity: 1; transform: scale(1.1); }
}
@keyframes sd-glow-pulse {
  0%, 100% { opacity: 0.15; }
  50% { opacity: 0.35; }
}
@keyframes sd-float-up {
  0% { transform: translateY(0) rotate(0deg); opacity: 1; }
  100% { transform: translateY(-100vh) rotate(360deg); opacity: 0; }
}
@keyframes sd-drift {
  0%, 100% { transform: translateY(0) translateX(0); }
  25% { transform: translateY(-8px) translateX(4px); }
  75% { transform: translateY(4px) translateX(-3px); }
}
@keyframes sd-shimmer {
  0% { opacity: 0.4; }
  50% { opacity: 0.8; }
  100% { opacity: 0.4; }
}
@keyframes sd-confetti-fall {
  0% { transform: translateY(-10px) rotate(0deg); opacity: 1; }
  100% { transform: translateY(100vh) rotate(720deg); opacity: 0.3; }
}
@keyframes sd-fade-in {
  0% { opacity: 0; transform: translateY(-20px); }
  100% { opacity: 1; transform: translateY(0); }
}
`

// ---------------------------------------------------------------------------
// RAMADAN — Hanging lanterns, crescent moon, twinkling stars
// Palette: Gold #D4A853, Amber #C48B2C, Deep purple #2D1B4E, Warm cream
// ---------------------------------------------------------------------------
function RamadanTheme() {
  return (
    <>
      {/* Atmospheric gradient overlay at top */}
      <div
        className="absolute inset-x-0 top-0 h-40"
        style={{
          background: 'linear-gradient(180deg, rgba(45,27,78,0.12) 0%, rgba(212,168,83,0.04) 60%, transparent 100%)',
        }}
      />

      {/* Hanging lanterns */}
      <Lantern x="8%" delay={0} size={42} />
      <Lantern x="25%" delay={0.6} size={34} />
      <Lantern x="52%" delay={1.2} size={38} />
      <Lantern x="78%" delay={0.3} size={36} />
      <Lantern x="92%" delay={0.9} size={30} />

      {/* Crescent moon — top-right area */}
      <div
        className="absolute"
        style={{
          top: '60px',
          right: '6%',
          animation: 'sd-drift 8s ease-in-out infinite',
          animationDelay: '0.5s',
        }}
      >
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <path
            d="M36 24c0-8.28-5.04-15.36-12.24-18.36C24.48 5.28 25.2 5.04 26.04 4.92 35.4 6.12 42.6 14.16 42.6 24s-7.2 17.88-16.56 19.08c-.84-.12-1.56-.36-2.28-.72C31.0 39.36 36 32.28 36 24z"
            fill="#D4A853"
            opacity="0.7"
          />
          <path
            d="M36 24c0-8.28-5.04-15.36-12.24-18.36C24.48 5.28 25.2 5.04 26.04 4.92 35.4 6.12 42.6 14.16 42.6 24s-7.2 17.88-16.56 19.08c-.84-.12-1.56-.36-2.28-.72C31.0 39.36 36 32.28 36 24z"
            fill="url(#moonGlow)"
            opacity="0.4"
          />
          <defs>
            <radialGradient id="moonGlow" cx="0.5" cy="0.5" r="0.5">
              <stop offset="0%" stopColor="#FDE68A" />
              <stop offset="100%" stopColor="#D4A853" stopOpacity="0" />
            </radialGradient>
          </defs>
        </svg>
      </div>

      {/* Twinkling stars */}
      <Star x="12%" y="40px" size={6} delay={0} />
      <Star x="20%" y="80px" size={4} delay={1.5} />
      <Star x="35%" y="25px" size={5} delay={0.8} />
      <Star x="45%" y="65px" size={3} delay={2.2} />
      <Star x="60%" y="35px" size={5} delay={0.4} />
      <Star x="70%" y="75px" size={4} delay={1.8} />
      <Star x="85%" y="50px" size={6} delay={1.1} />
      <Star x="95%" y="30px" size={3} delay={2.5} />

      {/* Bottom corner glow */}
      <div
        className="absolute bottom-0 left-0 h-32 w-48"
        style={{
          background: 'radial-gradient(ellipse at bottom left, rgba(212,168,83,0.08) 0%, transparent 70%)',
          animation: 'sd-glow-pulse 6s ease-in-out infinite',
        }}
      />
      <div
        className="absolute bottom-0 right-0 h-32 w-48"
        style={{
          background: 'radial-gradient(ellipse at bottom right, rgba(45,27,78,0.08) 0%, transparent 70%)',
          animation: 'sd-glow-pulse 6s ease-in-out infinite',
          animationDelay: '3s',
        }}
      />
    </>
  )
}

function Lantern({ x, delay, size }: { x: string; delay: number; size: number }) {
  const w = size
  const h = size * 1.6
  return (
    <div
      className="absolute top-0"
      style={{
        left: x,
        transformOrigin: 'top center',
        animation: `sd-sway ${3 + delay}s ease-in-out infinite, sd-fade-in 1s ease-out forwards`,
        animationDelay: `${delay}s`,
        opacity: 0,
      }}
    >
      {/* String */}
      <div
        className="mx-auto"
        style={{ width: '1px', height: '18px', background: 'linear-gradient(180deg, transparent, #C48B2C)' }}
      />
      {/* Lantern body */}
      <svg width={w} height={h} viewBox="0 0 40 64" fill="none" className="mx-auto">
        {/* Cap */}
        <rect x="14" y="0" width="12" height="4" rx="1" fill="#C48B2C" opacity="0.8" />
        {/* Top dome */}
        <path d="M10 12C10 6 14 4 20 4C26 4 30 6 30 12" fill="#D4A853" opacity="0.6" />
        {/* Body */}
        <path
          d="M8 12C8 12 6 24 6 32C6 40 10 48 20 48C30 48 34 40 34 32C34 24 32 12 32 12Z"
          fill="#D4A853"
          opacity="0.5"
        />
        <path
          d="M8 12C8 12 6 24 6 32C6 40 10 48 20 48C30 48 34 40 34 32C34 24 32 12 32 12Z"
          fill="url(#lanternGlow)"
          opacity="0.6"
        />
        {/* Decorative bands */}
        <ellipse cx="20" cy="24" rx="12" ry="1" fill="#C48B2C" opacity="0.4" />
        <ellipse cx="20" cy="36" rx="10" ry="1" fill="#C48B2C" opacity="0.4" />
        {/* Bottom tip */}
        <path d="M16 48L20 56L24 48" fill="#C48B2C" opacity="0.6" />
        {/* Inner glow */}
        <circle cx="20" cy="30" r="6" fill="#FDE68A" opacity="0.3">
          <animate attributeName="opacity" values="0.2;0.5;0.2" dur="3s" repeatCount="indefinite" />
        </circle>
        <defs>
          <radialGradient id="lanternGlow" cx="0.5" cy="0.4" r="0.5">
            <stop offset="0%" stopColor="#FDE68A" stopOpacity="0.6" />
            <stop offset="100%" stopColor="#D4A853" stopOpacity="0.1" />
          </radialGradient>
        </defs>
      </svg>
    </div>
  )
}

function Star({ x, y, size, delay }: { x: string; y: string; size: number; delay: number }) {
  return (
    <div
      className="absolute"
      style={{
        left: x,
        top: y,
        animation: `sd-twinkle ${2.5 + delay * 0.3}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
      }}
    >
      <svg width={size * 2} height={size * 2} viewBox="0 0 12 12" fill="none">
        <path
          d="M6 0L7.2 4.8L12 6L7.2 7.2L6 12L4.8 7.2L0 6L4.8 4.8Z"
          fill="#D4A853"
          opacity="0.7"
        />
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// EID — Festive crescents, confetti, celebratory atmosphere
// Palette: Emerald #1B7A4A, Gold #D4A853, Rose #E8698D, White
// ---------------------------------------------------------------------------
function EidTheme() {
  return (
    <>
      {/* Top festive gradient */}
      <div
        className="absolute inset-x-0 top-0 h-32"
        style={{
          background: 'linear-gradient(180deg, rgba(27,122,74,0.08) 0%, rgba(212,168,83,0.04) 50%, transparent 100%)',
        }}
      />

      {/* Celebratory crescent + star motif — top left */}
      <div
        className="absolute"
        style={{
          top: '20px',
          left: '5%',
          animation: 'sd-drift 10s ease-in-out infinite',
        }}
      >
        <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
          <path
            d="M40 28c0-9-5.5-16.7-13.3-20C27.5 7.6 28.3 7.3 29.2 7.2 39.4 8.5 47.2 17.3 47.2 28S39.4 47.5 29.2 48.8c-.9-.1-1.7-.4-2.5-.8C34.5 44.7 40 37 40 28z"
            fill="#1B7A4A"
            opacity="0.5"
          />
          {/* Star beside crescent */}
          <path d="M18 12L19.5 17.5L25 19L19.5 20.5L18 26L16.5 20.5L11 19L16.5 17.5Z" fill="#D4A853" opacity="0.6" />
        </svg>
      </div>

      {/* Confetti particles */}
      {EID_CONFETTI.map((c, i) => (
        <div
          key={i}
          className="absolute rounded-full"
          style={{
            width: c.size,
            height: c.size,
            left: c.x,
            top: '-10px',
            backgroundColor: c.color,
            opacity: 0.6,
            animation: `sd-confetti-fall ${c.dur}s linear infinite`,
            animationDelay: `${c.delay}s`,
          }}
        />
      ))}

      {/* Decorative stars at edges */}
      <EidStar x="15%" y="60px" size={8} delay={0.4} color="#D4A853" />
      <EidStar x="88%" y="45px" size={6} delay={1.2} color="#1B7A4A" />
      <EidStar x="40%" y="30px" size={7} delay={0.8} color="#E8698D" />
      <EidStar x="65%" y="70px" size={5} delay={1.6} color="#D4A853" />
      <EidStar x="95%" y="85px" size={6} delay={2.0} color="#1B7A4A" />

      {/* Bottom right decorative crescent */}
      <div
        className="absolute"
        style={{
          bottom: '40px',
          right: '4%',
          opacity: 0.15,
          animation: 'sd-drift 12s ease-in-out infinite',
          animationDelay: '2s',
        }}
      >
        <svg width="36" height="36" viewBox="0 0 48 48" fill="none">
          <path
            d="M36 24c0-8.28-5.04-15.36-12.24-18.36C24.48 5.28 25.2 5.04 26.04 4.92 35.4 6.12 42.6 14.16 42.6 24s-7.2 17.88-16.56 19.08c-.84-.12-1.56-.36-2.28-.72C31.0 39.36 36 32.28 36 24z"
            fill="#1B7A4A"
          />
        </svg>
      </div>
    </>
  )
}

const EID_CONFETTI = [
  { x: '8%', size: '6px', color: '#1B7A4A', dur: 7, delay: 0 },
  { x: '15%', size: '4px', color: '#D4A853', dur: 9, delay: 1.5 },
  { x: '22%', size: '5px', color: '#E8698D', dur: 8, delay: 3 },
  { x: '33%', size: '4px', color: '#1B7A4A', dur: 10, delay: 0.8 },
  { x: '42%', size: '6px', color: '#D4A853', dur: 7.5, delay: 2.2 },
  { x: '55%', size: '5px', color: '#FFFFFF', dur: 9, delay: 1 },
  { x: '65%', size: '4px', color: '#E8698D', dur: 8.5, delay: 3.5 },
  { x: '74%', size: '6px', color: '#1B7A4A', dur: 7, delay: 0.5 },
  { x: '83%', size: '5px', color: '#D4A853', dur: 10, delay: 2.8 },
  { x: '92%', size: '4px', color: '#1B7A4A', dur: 8, delay: 1.8 },
]

function EidStar({ x, y, size, delay, color }: { x: string; y: string; size: number; delay: number; color: string }) {
  return (
    <div
      className="absolute"
      style={{
        left: x,
        top: y,
        animation: `sd-twinkle ${3 + delay * 0.2}s ease-in-out infinite`,
        animationDelay: `${delay}s`,
      }}
    >
      <svg width={size * 2} height={size * 2} viewBox="0 0 16 16" fill="none">
        <path d="M8 0L9.8 6.2L16 8L9.8 9.8L8 16L6.2 9.8L0 8L6.2 6.2Z" fill={color} opacity="0.6" />
      </svg>
    </div>
  )
}

// ---------------------------------------------------------------------------
// HAJJ — Geometric Islamic border, Kaaba silhouette, spiritual atmosphere
// Palette: Emerald #0D6B3F, Gold #C9A84C, White, Deep green #064E2B
// ---------------------------------------------------------------------------
function HajjTheme() {
  return (
    <>
      {/* Geometric Islamic pattern strip at top */}
      <div className="absolute inset-x-0 top-0 overflow-hidden" style={{ height: '8px' }}>
        <svg width="100%" height="8" preserveAspectRatio="none">
          <defs>
            <pattern id="islamicBorder" x="0" y="0" width="32" height="8" patternUnits="userSpaceOnUse">
              <path d="M0 4L4 0H12L16 4L12 8H4Z" fill="#0D6B3F" opacity="0.4" />
              <path d="M16 4L20 0H28L32 4L28 8H20Z" fill="#C9A84C" opacity="0.3" />
            </pattern>
          </defs>
          <rect width="100%" height="8" fill="url(#islamicBorder)" />
        </svg>
      </div>

      {/* Subtle emerald gradient */}
      <div
        className="absolute inset-x-0 top-2 h-28"
        style={{
          background: 'linear-gradient(180deg, rgba(13,107,63,0.06) 0%, transparent 100%)',
        }}
      />

      {/* Kaaba silhouette — bottom left */}
      <div
        className="absolute"
        style={{
          bottom: '30px',
          left: '4%',
          opacity: 0.08,
          animation: 'sd-shimmer 8s ease-in-out infinite',
        }}
      >
        <svg width="64" height="72" viewBox="0 0 64 72" fill="none">
          {/* Kaaba body */}
          <rect x="8" y="20" width="48" height="44" rx="1" fill="#064E2B" />
          {/* Kiswa band */}
          <rect x="8" y="36" width="48" height="8" fill="#C9A84C" opacity="0.5" />
          {/* Door */}
          <rect x="24" y="40" width="12" height="20" rx="6" fill="#C9A84C" opacity="0.4" />
          {/* Top cloth drape */}
          <path d="M6 20L32 8L58 20H6Z" fill="#064E2B" opacity="0.8" />
        </svg>
      </div>

      {/* Geometric corner ornaments */}
      <div className="absolute top-3 right-4" style={{ opacity: 0.12 }}>
        <svg width="48" height="48" viewBox="0 0 48 48" fill="none">
          <circle cx="24" cy="24" r="20" stroke="#0D6B3F" strokeWidth="0.5" fill="none" />
          <circle cx="24" cy="24" r="14" stroke="#C9A84C" strokeWidth="0.5" fill="none" />
          <path d="M24 4L28 20H44L31 29L35 45L24 36L13 45L17 29L4 20H20Z" fill="none" stroke="#0D6B3F" strokeWidth="0.4" opacity="0.5" />
        </svg>
      </div>

      {/* Subtle gold stars */}
      <Star x="20%" y="30px" size={4} delay={0.5} />
      <Star x="50%" y="20px" size={3} delay={1.8} />
      <Star x="75%" y="40px" size={4} delay={1.0} />

      {/* Bottom emerald glow */}
      <div
        className="absolute bottom-0 inset-x-0 h-24"
        style={{
          background: 'linear-gradient(0deg, rgba(13,107,63,0.04) 0%, transparent 100%)',
          animation: 'sd-glow-pulse 8s ease-in-out infinite',
        }}
      />
    </>
  )
}

// ---------------------------------------------------------------------------
// SAUDI NATIONAL DAY — Swords + palm emblem, green/white confetti, patriotic
// Palette: Saudi Green #006C35, Gold #C9A84C, White, Deep green #004D25
// ---------------------------------------------------------------------------
function NationalDayTheme() {
  // Saudi National Day 96 (23 Sep 2026), identity "عزّنا بطبعنا". The sadu band
  // and the official lockup live in BookingHeader (above the fold, never under
  // the nav); the fixed corners belong to the chat, WhatsApp and accessibility
  // bubbles, so this layer only adds a quiet green wash under the header.
  return (
    <div
      className="absolute inset-x-0 top-0 h-28"
      style={{ background: 'linear-gradient(180deg, rgba(12,74,52,0.10) 0%, transparent 100%)' }}
    />
  )
}

// ---------------------------------------------------------------------------
// Theme registry
// ---------------------------------------------------------------------------
const THEME_MAP: Record<SeasonalTheme, React.FC> = {
  ramadan: RamadanTheme,
  eid: EidTheme,
  hajj: HajjTheme,
  national_day: NationalDayTheme,
}
